import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { Company, InvoiceItem } from '../types';
import { NotaGridContainer } from '../cardStyles';
import { rotuloNota } from '../constants';
import TotaisSection from '../Sections/TotaisSection';
import FreteSection from '../Sections/FreteSection';
import InfoAdicionaisSection from '../Sections/InfoAdicionaisSection';
import PagamentoSection from '../Sections/PagamentoSection';
import DestinatarioSection from '../Sections/DestinatarioSection';
import IdentificacaoSection, { IdentificacaoValues } from '../Sections/IdentificacaoSection';
import ItensSection from '../Sections/ItensSection';
import { useEmissionPolling } from '../useEmissionPolling';
import InvoiceHeaderActions from '../InvoiceHeaderActions';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import { ColunaLateral, NegadaAlert, NegadaIcone } from '../style';

const EMPTY_IDENT: IdentificacaoValues = {
  companyEmissorId: '',
  naturezaOperacao: '',
  tipoOperacao: '',
  finalidade: '',
};

const ViewInvoice = () => {
  const { id: invoiceId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const { setHeaderActions } = useOutletContext<{ setHeaderActions?: (acoes: React.ReactNode) => void }>() ?? {};
  const [showCancelNfe, setShowCancelNfe] = useState(false);
  const [cancelandoNfe, setCancelandoNfe] = useState(false);

  const [invoice, setInvoice] = useState<any>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [selectedDestinatario, setSelectedDestinatario] = useState<{ company: Company } | null>(null);

  const formatBrlNumber = (val: any) =>
    val ? Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

  const loadInvoice = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const response = await ApiService.fiscal.getInvoice(Number(invoiceId));
      const data = response.data;
      if (!data) return;

      setInvoice(data);
      if (data.CompanyCliente) {
        setSelectedDestinatario({ company: data.CompanyCliente });
      }
      if (Array.isArray(data.InvoiceProduct)) {
        const mapped = data.InvoiceProduct.map((p: any) => ({
          id: p.id || p.orderItemId,
          codigo: p.codigoProduto || `ITEM-${p.orderItemId || p.id}`,
          descricao: p.descricaoProduto || 'Produto Sem Descrição',
          ncmId: p.ncmId || null,
          cfopId: null,
          cfop: p.cfop || '',
          ncmCode: p.Ncm?.ncm || '',
          cst: p.cstICMS || '',
          cstIpi: p.cstIPI || '',
          cEnqIpi: p.codigoEnquadramentoIPI || '',
          cstPisCofins: p.cstPIS_COFINS || '',
          cstIbsCbs: p.cstIBS_CBS || '',
          classificacao: p.classificacaoTributaria || '',
          // Item sem unidade própria (aço) não persiste unitOfMeasurementId — o fallback
          // espelha o da emissão, que emite 'UN'.
          unidade: p.UnitOfMeasurement?.name || 'un',
          quantidade: p.quantidade || 0,
          vlUnit: formatBrlNumber(p.valorUnitario) || '0,00',
          vlTotal: formatBrlNumber(p.valorTotal) || '0,00',
          bcIcms: formatBrlNumber(p.baseCalculoICMS || p.valorTotal),
          vlIcms: formatBrlNumber(p.valorICMS),
          vlIpi: formatBrlNumber(p.valorIPI),
          vlIbs: formatBrlNumber(p.valorIBS),
          vlCbs: formatBrlNumber(p.valorCBS),
          aliqIcms: p.aliquotaICMS ?? '',
          aliqIpi: p.aliquotaIPI ?? '',
          _lastLookupHash: '',
        }));
        setItems(mapped);
      }
    } catch (error) {
      console.error('Erro ao carregar nota fiscal:', error);
      setFeedback({ type: 'error', message: 'Erro ao carregar a nota fiscal.', isOpen: true, statusOk: false });
    }
  }, [invoiceId, setFeedback]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  // Sincroniza com o Nota Gateway (consultarStatus grava EMITIDA/NEGADA) e depois
  // recarrega para refletir. Só reler (loadInvoice) deixaria a nota presa em EMITINDO.
  const syncStatus = useCallback(async () => {
    if (!invoiceId) return;
    try {
      await ApiService.fiscal.nfeStatus(Number(invoiceId));
    } catch (error) {
      console.error('Erro ao consultar status da NF-e:', error);
    }
    await loadInvoice();
  }, [invoiceId, loadInvoice]);

  useEmissionPolling({ isEmitindo: invoice?.status === 'EMITINDO', onTick: syncStatus });

  const handleCancelar = async () => {
    if (!invoiceId) return;
    try {
      await ApiService.fiscal.cancelar(Number(invoiceId));
      setFeedback({ message: 'Cancelamento solicitado.', isOpen: true, statusOk: true });
      loadInvoice();
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao cancelar a nota.', isOpen: true, statusOk: false });
    }
  };

  const confirmarCancelamentoNfe = async () => {
    setCancelandoNfe(true);
    try {
      await handleCancelar();
    } finally {
      setCancelandoNfe(false);
      setShowCancelNfe(false);
    }
  };

  // Navegação via ref: nos deps do effect ela recriaria o nó a cada render e o setHeaderActions entraria em loop.
  const acoesHeader = useRef({ voltar: () => navigate(-1), editar: () => navigate(`/panel/invoice/edit/${invoiceId}`) });
  acoesHeader.current = { voltar: () => navigate(-1), editar: () => navigate(`/panel/invoice/edit/${invoiceId}`) };
  const statusNota: string | undefined = invoice?.status;
  const linkDanfe: string | undefined = invoice?.linkDanfe ?? undefined;

  useEffect(() => {
    setHeaderActions?.(
      <InvoiceHeaderActions
        mode="view"
        status={statusNota}
        onVoltar={() => acoesHeader.current.voltar()}
        onEditar={() => acoesHeader.current.editar()}
        onCancelarNfe={() => setShowCancelNfe(true)}
        onVerDanfe={linkDanfe ? () => window.open(linkDanfe, '_blank', 'noopener') : undefined}
      />,
    );
    return () => setHeaderActions?.(null);
  }, [setHeaderActions, statusNota, linkDanfe]);

  const rotuloDaNota = rotuloNota(invoice?.serie, invoice?.numeroNF, invoiceId);

  return (
    <>
    <NotaGridContainer>
      {/* COLUNA DA ESQUERDA */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <DestinatarioSection
          companies={[]}
          selectedDestinatario={selectedDestinatario}
          onSelect={() => {}}
          readOnly
        />
        <ItensSection
          items={items}
          setItems={setItems}
          cfops={[]}
          cfopsPerItem={new Map()}
          destStateId={null}
          isEmitida
          onRemove={() => {}}
          fetchNcmsForItem={async () => {}}
          ncmOptions={[]}
        />
        <PagamentoSection invoice={invoice} invoiceId={Number(invoiceId)} onSaved={loadInvoice} readOnly />
        <FreteSection invoice={invoice} invoiceId={Number(invoiceId)} companies={[]} onSaved={loadInvoice} readOnly />
        <InfoAdicionaisSection invoice={invoice} readOnly />
      </div>

      {/* COLUNA DA DIREITA */}
      <ColunaLateral>
        {statusNota === 'NEGADA' && invoice?.motivoStatus && (
          <NegadaAlert>
            <NegadaIcone>⚠</NegadaIcone>
            <span>{invoice.motivoStatus}</span>
          </NegadaAlert>
        )}
        <IdentificacaoSection
          status={statusNota}
          emissoras={[]}
          invoice={invoice}
          value={EMPTY_IDENT}
          onChange={() => {}}
          readOnly
        />
        <TotaisSection items={items} frete={Number(invoice?.valorFreteNota ?? 0) || 0} />
      </ColunaLateral>
    </NotaGridContainer>

    <ConfirmationModal
      isOpen={showCancelNfe}
      title="Cancelar NF-e"
      message={`Tem certeza que deseja cancelar a nota ${rotuloDaNota}? O cancelamento é irreversível e só é permitido em até 24h da autorização.`}
      actions={[
        { label: 'Voltar', onClick: () => setShowCancelNfe(false), variant: 'outline' },
        {
          label: cancelandoNfe ? 'Cancelando…' : 'Cancelar NF-e',
          onClick: confirmarCancelamentoNfe,
          variant: 'danger',
        },
      ]}
    />
    </>
  );
};

export default ViewInvoice;
